/**
 * API usage summaries shown on the Developer Platform dashboard
 * (SAIV-11349) and on a key's Usage tab.
 *
 * `UsageSummaryDto` mirrors the backend's `OrgUsageSummaryRead`
 * (`GET integrations/v1/orgs/{org_id}/api-keys/usage`) and
 * `ApiKeyUsageRead` (`GET …/api-keys/{api_key_id}/usage`): both share the
 * call / latency / success-rate blocks, the org summary adds the key
 * lifecycle counts and the per-key one adds a daily series and a
 * per-event breakdown. `UsageSummary` is the normalised shape the UI
 * consumes.
 */
export interface UsageSummaryDto {
  /** Length of the reporting window in days (30 for the dashboard). */
  period_days?: number
  /** `CountWithTrend` */
  api_calls: {
    total: number
    /** Same window immediately before this one; null when there is none. */
    previous_total?: number | null
  }
  /** `LatencyRead` */
  latency: {
    avg_ms: number | null
    previous_avg_ms?: number | null
  }
  /** `SuccessRateRead` */
  success_rate: {
    /** 0..100, null when there were no calls. */
    percent: number | null
    previous_percent?: number | null
    /** Number of 4xx + 5xx responses in the window. */
    error_count: number
  }
  /** `KeyLifecycleRead`; org summary only. */
  keys?: {
    generated: number
    active: number
    inactive: number
    deleted: number
  }
  /** `DailyUsagePoint[]`; per-key usage only. */
  series?: UsagePointDto[]
  /** `EventBreakdown[]`; per-key usage only. */
  by_event?: UsageEventDto[]
}

export interface UsagePointDto {
  /** ISO date (`YYYY-MM-DD`). */
  date: string
  calls: number
  errors: number
  avg_ms: number | null
}

export interface UsageEventDto {
  event: string
  calls: number
  errors: number
}

export interface UsagePoint {
  date: string
  calls: number
  errors: number
  avgMs: number | null
}

export interface UsageEvent {
  event: string
  calls: number
  errors: number
}

export interface UsageSummary {
  periodDays: number
  apiCalls: { total: number; previousTotal: number | null }
  latency: { avgMs: number | null; previousAvgMs: number | null }
  successRate: { percent: number | null; previousPercent: number | null; errorCount: number }
  /** Zeros for per-key usage, which has no lifecycle counts. */
  keys: { generated: number; active: number; inactive: number; deleted: number }
  /** Empty for the org summary, which has no series. */
  series: UsagePoint[]
  byEvent: UsageEvent[]
}

export type UsageStatus =
  /** Request in flight and nothing cached yet. */
  | 'loading'
  /** Endpoint answered; at least one call or key exists in the window. */
  | 'available'
  /** Endpoint answered but the organisation has no traffic and no keys yet. */
  | 'empty'
  /** Endpoint does not exist on this backend (404/501): hide the usage row. */
  | 'unavailable'
  /** Any other failure: show an inline error with retry. */
  | 'error'

const NO_KEYS = { generated: 0, active: 0, inactive: 0, deleted: 0 }

export function toUsageSummary(dto: UsageSummaryDto): UsageSummary {
  return {
    periodDays: dto.period_days ?? 30,
    apiCalls: {
      total: dto.api_calls.total,
      previousTotal: dto.api_calls.previous_total ?? null
    },
    latency: {
      avgMs: dto.latency.avg_ms,
      previousAvgMs: dto.latency.previous_avg_ms ?? null
    },
    successRate: {
      percent: dto.success_rate.percent,
      previousPercent: dto.success_rate.previous_percent ?? null,
      errorCount: dto.success_rate.error_count
    },
    keys: dto.keys ? { ...dto.keys } : { ...NO_KEYS },
    series: (dto.series ?? []).map(point => ({
      date: point.date,
      calls: point.calls,
      errors: point.errors,
      avgMs: point.avg_ms
    })),
    byEvent: (dto.by_event ?? []).map(event => ({ ...event }))
  }
}

/** True when nothing has happened yet: first-run / zero-data state. */
export function isUsageEmpty(summary: UsageSummary): boolean {
  return summary.apiCalls.total === 0 && summary.keys.generated === 0
}
